import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { faceDataPolicyHref } from "@/content/faceDataPolicy";
import SiteFrame from "./SiteFrame";
import { BoxBrackets, BracketMark } from "./PhotoDemo";
import { AppStoreButton } from "./AppStore";
import { PhoneFrame, appShot, useAppShot, type AppScreen } from "./appScreens";
import { EAGLE_DARK, PERSONAL_HERO } from "./images";
import { APP_LEGAL_BASE } from "./paths";
import { ProductEyebrow } from "./products";
import { AMBER_DISPLAY, BAND, CARD, EYEBROW, H1, H2, HERO_FIGURE, HERO_TEXT, LEAD, SECTION_Y, TEXT_LINK, WRAP } from "./ui";

// /app: the Photohawk app ("Photohawk: Friends & Family" on the App Store), the iPhone app for your own photos. Its
// story only: it never mentions Studio, event photos or selling (owner, 2 Nov 2026; the site's header and footer stay
// the site's). Every feature here is one the app has (photohawk-personal README and its App Store description): no
// things or text in photos, no pets. The privacy section follows the app's consent wording
// (photohawk-personal lib/src/privacy/consent_copy.dart, version 2026-10-28) and must keep agreeing with it and the
// face data policy. No prices: the App Store shows them; "free to start, 7-day free trial of Personal".

const FEATURES = ["grouped", "search", "share", "chats", "memories", "receive"] as const;
/** The app's real screens. Not "search": its capture searches for a thing (a dog), which the app doesn't offer now. */
const SCREENS: readonly AppScreen[] = ["people", "person", "share", "detail"];
const SAFETY = ["strong", "trust", "never", "unsend", "location"] as const;
const PRIVACY = ["phone", "cloud", "share", "delete"] as const;
const PLANS = ["personal", "family", "free"] as const;
const APP_FAQ = ["free", "upload", "receivers", "android", "delete", "account", "where"] as const;

const AppPage = () => {
  const { t, i18n } = useTranslation();
  const a = (key: string): string => t(`home.app.${key}`);
  const peopleShot = useAppShot("people");

  const getIt = (testId: string) => (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-3" data-testid={testId}>
      <AppStoreButton testId={`${testId}-store`} />
      <span className="flex flex-col gap-0.5 text-sm">
        <strong className="font-semibold text-foreground">{a("platforms")}</strong>
        <span className="text-muted-foreground">{a("pricing")}</span>
      </span>
    </div>
  );

  return (
    <SiteFrame page="app" path="app">
      {/* HERO */}
      <section aria-labelledby="app-title">
        <div className={`${WRAP} flex flex-wrap items-center gap-x-14 gap-y-10 pb-[clamp(56px,7vw,96px)] pt-[clamp(32px,5vw,80px)]`}>
          <div className={HERO_TEXT}>
            <ProductEyebrow product="personal" text={a("eyebrow")} />
            <h1 id="app-title" className={H1}>
              {a("title")} <span style={{ color: AMBER_DISPLAY }}>{a("titleHighlight")}</span>
            </h1>
            <p className="m-0 max-w-[32em] text-[clamp(17px,1.5vw,20px)] leading-relaxed text-muted-foreground">{a("subtitle")}</p>
            {getIt("app-get")}
          </div>
          <figure className={`${HERO_FIGURE} pb-[34px]`}>
            <div className="relative aspect-[3/2] w-full overflow-hidden rounded-xl shadow-lg">
              <img
                src={PERSONAL_HERO.src}
                srcSet={PERSONAL_HERO.srcSet}
                sizes="(min-width: 1024px) 640px, 100vw"
                width={PERSONAL_HERO.width}
                height={PERSONAL_HERO.height}
                alt={t("home.personal.heroAlt")}
                className="block h-full w-full object-cover"
              />
              {PERSONAL_HERO.faces.map((f) => (
                <span key={f.name}>
                  <BoxBrackets box={f.box} arm="28%" delay={f.delay} />
                  <span
                    aria-hidden="true"
                    className="absolute rounded bg-[#EA8A23] px-[9px] py-[3px] text-[13px] font-bold text-[#121721]"
                    style={{ left: `${f.box[0]}%`, top: `${f.box[1] + f.box[3] + 1.6}%` }}
                  >
                    {f.name}
                  </span>
                </span>
              ))}
            </div>
            {/* The app, over the photo's left edge: the People screen, where Tom and Ana end up. */}
            <div className="absolute bottom-0 left-3 w-[23%] max-w-[190px] -rotate-[4deg] sm:w-[27%] lg:left-[-3%]" data-testid="app-hero-phone">
              <PhoneFrame shot={peopleShot} alt={t("home.personal.screens.alt.people")} sizes="(min-width: 1024px) 180px, 27vw" eager />
            </div>
            <figcaption className="absolute bottom-0 right-3 flex items-center gap-2.5 rounded-xl border border-border bg-card py-2.5 pl-2.5 pr-3.5 text-foreground shadow-lg">
              <img src={EAGLE_DARK} alt="" width={26} height={27} className="h-[27px] w-[26px] object-contain" />
              <span className="text-sm font-semibold">{t("home.personal.heroCaption")}</span>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* WHAT IT DOES */}
      <section aria-labelledby="app-features" className={`${BAND} border-y border-border`}>
        <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-9`}>
          <h2 id="app-features" className={`${H2} max-w-[16em]`}>
            {a("features.heading")}
          </h2>
          <ul className="m-0 grid list-none grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border p-0 shadow-sm sm:grid-cols-2 lg:grid-cols-3" data-testid="app-features-list">
            {FEATURES.map((f) => (
              <li key={f} className="flex flex-col gap-2.5 bg-card p-7" data-testid={`app-feature-${f}`}>
                <h3 className="m-0 font-display text-[21px] font-semibold">{a(`features.${f}.title`)}</h3>
                <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{a(`features.${f}.text`)}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* THE APP'S SCREENS: a scroller on phones, a row of four from 1024 px */}
      <section aria-labelledby="app-screens" className="pt-[clamp(64px,8vw,104px)]" data-testid="app-screens">
        <div className={`${WRAP} flex flex-col gap-9`}>
          <h2 id="app-screens" className={H2}>
            {a("screens.heading")}
          </h2>
          <ul
            className="-mx-[clamp(16px,4vw,56px)] my-0 flex list-none snap-x snap-mandatory scroll-px-[clamp(16px,4vw,56px)] gap-5 overflow-x-auto px-[clamp(16px,4vw,56px)] pb-4 pt-1 lg:mx-0 lg:grid lg:grid-cols-4 lg:gap-[clamp(16px,2.5vw,40px)] lg:overflow-visible lg:px-[clamp(0px,4vw,64px)]"
            tabIndex={0}
            aria-label={a("screens.heading")}
          >
            {SCREENS.map((screen) => (
              <li key={screen} className="flex w-[clamp(170px,46vw,220px)] flex-none snap-start flex-col lg:w-auto" data-testid={`app-screen-${screen}`}>
                <PhoneFrame shot={appShot(screen, i18n.language)} alt={t(`home.personal.screens.alt.${screen}`)} sizes="(min-width: 1024px) 230px, 220px" />
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* AUTOMATIC SHARING, SAFELY */}
      <section aria-labelledby="app-safety">
        <div className={`${WRAP} ${SECTION_Y} flex flex-wrap items-start gap-x-16 gap-y-8`}>
          <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-4">
            <h2 id="app-safety" className={H2}>
              {a("safety.heading")}
            </h2>
            <p className={LEAD}>{a("safety.text")}</p>
          </div>
          <ul className="m-0 flex min-w-0 flex-[1.3_1_380px] list-none flex-col gap-4 p-0 text-[17px] leading-snug" data-testid="app-safety">
            {SAFETY.map((s) => (
              <li key={s} className="flex items-start gap-3">
                <span className="mt-1 flex-none">
                  <BracketMark size={16} />
                </span>
                {a(`safety.${s}`)}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PRIVATE BY DESIGN (the app's consent wording) */}
      <section id="privacy" aria-labelledby="app-privacy-heading" className={`${BAND} scroll-mt-4 border-y border-border`} data-testid="app-privacy">
        <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-9`}>
          <div className="flex max-w-[720px] flex-col gap-3">
            <span className={EYEBROW}>{a("privacy.eyebrow")}</span>
            <h2 id="app-privacy-heading" className={H2}>
              {a("privacy.heading")}
            </h2>
          </div>
          <ul className="m-0 grid list-none grid-cols-1 gap-4 p-0 md:grid-cols-2">
            {PRIVACY.map((p) => (
              <li key={p} className={`${CARD} flex flex-col gap-2.5 p-7`} data-testid={`app-privacy-${p}`}>
                <h3 className="m-0 font-display text-[20px] font-semibold leading-snug">{a(`privacy.${p}.title`)}</h3>
                <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{a(`privacy.${p}.text`)}</p>
              </li>
            ))}
          </ul>
          <p className="m-0 flex flex-wrap gap-x-6 gap-y-1">
            <Link to={faceDataPolicyHref("family")} className={TEXT_LINK} data-testid="app-face-data-policy">
              {a("privacy.policy")} <span aria-hidden="true">→</span>
            </Link>
            <Link to={`${APP_LEGAL_BASE}/privacy`} className={TEXT_LINK}>
              {a("privacy.privacyPolicy")} <span aria-hidden="true">→</span>
            </Link>
          </p>
        </div>
      </section>

      {/* PLANS: no prices (the App Store shows them, in the buyer's currency) */}
      <section id="plans" aria-labelledby="app-plans" className="scroll-mt-4" data-testid="app-plans">
        <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-9`}>
          <div className="flex max-w-[720px] flex-col gap-3">
            <h2 id="app-plans" className={H2}>
              {a("plans.heading")}
            </h2>
            <p className={LEAD}>{a("plans.text")}</p>
          </div>
          <ul className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(min(280px,100%),1fr))] gap-4 p-0">
            {PLANS.map((p) => (
              <li
                key={p}
                className={`${CARD} flex flex-col gap-3 p-7 ${p === "personal" ? "border-2 border-[#EA8A23] shadow-md" : ""}`}
                data-testid={`app-plan-${p}`}
              >
                <span className="flex flex-wrap items-center justify-between gap-3">
                  <span className="font-display text-2xl font-bold">{a(`plans.${p}.name`)}</span>
                  {p === "personal" && <span className="rounded-full bg-[#EA8A23] px-2.5 py-0.5 text-xs font-bold text-[#121721]">{a("plans.personal.badge")}</span>}
                </span>
                <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{a(`plans.${p}.text`)}</p>
              </li>
            ))}
          </ul>
          <p className="m-0 max-w-[48em] text-sm leading-relaxed text-muted-foreground" data-testid="app-plans-note">
            {a("plans.note")}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" aria-labelledby="app-faq" className={`${BAND} scroll-mt-4 border-y border-border`}>
        <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-8`}>
          <h2 id="app-faq" className={H2}>
            {a("faq.heading")}
          </h2>
          <div className="flex max-w-[860px] flex-col gap-3" data-testid="app-faq-list">
            {APP_FAQ.map((q) => (
              <details key={q} className={`${CARD} group px-6 py-1 [&_summary::-webkit-details-marker]:hidden`} data-testid={`app-faq-${q}`}>
                <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 py-3 text-[17px] font-semibold">
                  {a(`faq.${q}.q`)}
                  <span aria-hidden="true" className="flex-none text-xl text-accent-foreground transition-transform group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="m-0 pb-5 text-[15px] leading-relaxed text-muted-foreground">{a(`faq.${q}.a`)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* GET IT */}
      <section aria-labelledby="app-cta">
        <div className={`${WRAP} ${SECTION_Y} flex flex-col items-start gap-6`}>
          <h2 id="app-cta" className={`${H2} max-w-[18em]`}>
            {a("cta.heading")}
          </h2>
          {getIt("app-get-bottom")}
        </div>
      </section>
    </SiteFrame>
  );
};

export default AppPage;
